import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { ChildrenOutletContexts, NavigationError, PRIMARY_OUTLET } from '@angular/router';

/** Chiave in `sessionStorage`: istante dell'ultima ricarica fatta per un modulo mancante. */
export const CHIAVE_ULTIMA_RICARICA = 'civeshub.ricaricaModuloMancante';

/** Entro questa finestra un secondo modulo mancante non ricarica più: evita il ciclo infinito. */
export const FINESTRA_RICARICA_MS = 60_000;

/**
 * Testi con cui i browser segnalano un chunk lazy non più presente sul server: webpack
 * (`ChunkLoadError`, «Loading chunk N failed», anche CSS) e `import()` nativo di Chrome, Safari e
 * Firefox.
 */
const MODULO_MANCANTE =
  /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

export function isModuloMancante(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown };
  const testo = `${typeof e.name === 'string' ? e.name : ''} ${typeof e.message === 'string' ? e.message : String(error)}`;
  return MODULO_MANCANTE.test(testo);
}

/**
 * Gestore degli errori di navigazione (`withNavigationErrorHandler`): dopo un rilascio, chi aveva
 * l'app aperta sulla versione precedente chiede chunk che non esistono più, e la navigazione
 * fallisce senza nessun segno. Qui si ricarica UNA volta la pagina di destinazione:
 * `index.html` è servito `no-store`, quindi la ricarica prende la versione nuova.
 *
 * Non restituisce nulla: Angular rilancia comunque l'errore, che resta nel registro «errori
 * client» (la segnalazione è accodata prima che la pagina si scarichi, e la coda offline la
 * persiste al `visibilitychange`).
 *
 * Non ricarica: se la pagina aperta ha dati non ancora inviati ({@link haModificheInSospeso}: il
 * modulo lazy si carica prima dei guard di uscita, nessuno chiederebbe conferma), offline (il
 * chunk manca per la rete, non per la versione), entro
 * {@link FINESTRA_RICARICA_MS} da una ricarica precedente, o se `sessionStorage` non è
 * disponibile (senza guardia il ciclo non si potrebbe escludere).
 *
 * @param vai come si raggiunge l'URL; parametro solo per i test (`window.location` non è
 *        sostituibile con uno spy).
 */
export function ricaricaSeModuloMancante(
  evento: NavigationError,
  vai: (url: string) => void = (url) => window.location.assign(url),
): void {
  if (!isModuloMancante(evento.error)) return;
  if (navigator.onLine === false) return;
  if (haModificheInSospeso(inject(ChildrenOutletContexts))) return;
  try {
    const ultima = Number(sessionStorage.getItem(CHIAVE_ULTIMA_RICARICA));
    if (ultima && Date.now() - ultima < FINESTRA_RICARICA_MS) return;
    sessionStorage.setItem(CHIAVE_ULTIMA_RICARICA, String(Date.now()));
  } catch {
    return;
  }
  vai(inject(Location).prepareExternalUrl(evento.url));
}

/**
 * Vero se una delle pagine attive (dall'outlet radice in giù) dichiara dati non salvati con
 * `hasUnsavedChanges()`. In quel caso la navigazione fallisce come prima, ma i dati restano: la
 * ricarica scatta al primo clic dopo l'invio.
 */
export function haModificheInSospeso(contexts: ChildrenOutletContexts): boolean {
  let contesto = contexts.getContext(PRIMARY_OUTLET);
  while (contesto) {
    const outlet = contesto.outlet;
    if (outlet?.isActivated) {
      const pagina = outlet.component as { hasUnsavedChanges?: () => boolean };
      if (typeof pagina.hasUnsavedChanges === 'function' && pagina.hasUnsavedChanges()) return true;
    }
    contesto = contesto.children.getContext(PRIMARY_OUTLET);
  }
  return false;
}
